import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Receipt, RotateCcw, Search, X } from 'lucide-react'
import {
  formatDateTime, formatMoney, formatNumber, PAYMENT_METHODS, PERMISSIONS, SALE_STATUSES,
  type SaleStatus,
} from '@najah/core'
import {
  Alert, Badge, Button, Dialog, EmptyState, Input, Pagination, Select,
  Table, TableSkeleton, TableWrapper, Tbody, Td, Th, Thead, Tr, Textarea, cn, useToast,
} from '@najah/ui'
import { salesApi, type SaleListFilter } from '../lib/salesApi'
import { useAuth } from '../lib/auth'

const STATUS_TONE: Record<SaleStatus, 'success' | 'warning' | 'danger' | 'neutral'> = {
  COMPLETED: 'success',
  PARTIALLY_REFUNDED: 'warning',
  REFUNDED: 'danger',
  HELD: 'neutral',
  VOIDED: 'danger',
}

function ReturnDialog({ saleId, onClose }: { saleId: string | null; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [quantities, setQuantities] = useState<Record<string, number>>({})
  const [reason, setReason] = useState('')

  const { data: sale } = useQuery({
    queryKey: ['sales', 'detail', saleId],
    queryFn: () => salesApi.get(saleId!),
    enabled: !!saleId,
  })

  const mutation = useMutation({
    mutationFn: () => {
      const items = Object.entries(quantities)
        .filter(([, qty]) => qty > 0)
        .map(([saleItemId, quantity]) => ({ saleItemId, quantity }))

      return salesApi.createReturn({
        id: crypto.randomUUID(),
        reference: `RET-${Date.now().toString(36).toUpperCase()}`,
        saleId: saleId!,
        items,
        reason: reason.trim(),
        occurredAt: new Date().toISOString(),
      })
    },
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['sales'] })
      void queryClient.invalidateQueries({ queryKey: ['products'] })
      void queryClient.invalidateQueries({ queryKey: ['inventory'] })
      toast.success('تمّ تسجيل المرتجع', `أُعيد ${formatMoney(res.total)} والبضاعة عادت للمخزون`)
      setQuantities({})
      setReason('')
      onClose()
    },
    onError: (err) => toast.error('تعذّر المرتجع', err instanceof Error ? err.message : undefined),
  })

  const selected = Object.values(quantities).reduce((a, b) => a + b, 0)

  return (
    <Dialog
      open={!!saleId}
      onClose={onClose}
      size="lg"
      title="إرجاع من الفاتورة"
      description={sale?.reference}
      dismissible={!mutation.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>إلغاء</Button>
          <Button
            variant="danger"
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={selected === 0}
          >
            تسجيل المرتجع
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Alert tone="info">
          البضاعة المُرجَعة تعود إلى المخزون كحركة «إرجاع من زبون»، والمبلغ يخرج من درج الصندوق.
        </Alert>

        <TableWrapper>
          <Table>
            <Thead>
              <tr>
                <Th>المنتج</Th>
                <Th align="center">مُباع</Th>
                <Th align="center">مُرجَع سابقاً</Th>
                <Th align="center">كمية الإرجاع</Th>
              </tr>
            </Thead>
            <Tbody>
              {sale?.items.map((item) => {
                const remaining = item.quantity - item.returnedQty
                return (
                  <Tr key={item.id}>
                    <Td>
                      <p className="font-medium text-fg">{item.nameSnapshot}</p>
                      <p className="font-mono text-xs text-fg-subtle" dir="ltr">{item.skuSnapshot}</p>
                    </Td>
                    <Td align="center" className="tabular-nums" dir="ltr">{formatNumber(item.quantity)}</Td>
                    <Td align="center" className="tabular-nums text-fg-muted" dir="ltr">
                      {formatNumber(item.returnedQty)}
                    </Td>
                    <Td align="center">
                      <input
                        type="number"
                        min="0"
                        max={remaining}
                        disabled={remaining === 0}
                        value={quantities[item.id] ?? 0}
                        onChange={(e) => {
                          // لا يُقبل أكثر من المتبقّي — الخادم يرفضه أيضاً، لكن المنع هنا أوضح
                          const value = Math.min(remaining, Math.max(0, Number(e.target.value) || 0))
                          setQuantities((q) => ({ ...q, [item.id]: value }))
                        }}
                        className="w-20 rounded-lg border border-border bg-surface px-2 py-1.5 text-center text-sm tabular-nums disabled:opacity-40"
                        dir="ltr"
                      />
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </TableWrapper>

        <Textarea
          label="سبب الإرجاع" rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="تالف، مقاس خاطئ…"
        />
      </div>
    </Dialog>
  )
}

export function SalesPage() {
  const { can } = useAuth()
  const [filter, setFilter] = useState<SaleListFilter>({ page: 1, pageSize: 25 })
  const [searchInput, setSearchInput] = useState('')
  const [detailId, setDetailId] = useState<string | null>(null)
  const [returnId, setReturnId] = useState<string | null>(null)

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['sales', filter],
    queryFn: () => salesApi.list(filter),
    placeholderData: keepPreviousData,
  })

  const { data: detail } = useQuery({
    queryKey: ['sales', 'detail', detailId],
    queryFn: () => salesApi.get(detailId!),
    enabled: !!detailId,
  })

  function patch(next: SaleListFilter) {
    setFilter((f) => ({ ...f, ...next, page: next.page ?? 1 }))
  }

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold text-fg">الفواتير</h1>
        <p className="mt-0.5 text-sm text-fg-muted">
          {data ? `${formatNumber(data.total)} فاتورة` : 'جارٍ التحميل…'}
        </p>
      </div>

      <div className="grid gap-3 rounded-xl border border-border bg-surface p-4 sm:grid-cols-4">
        <form
          className="sm:col-span-2"
          onSubmit={(e) => { e.preventDefault(); patch({ search: searchInput.trim() || undefined }) }}
        >
          <Input
            label="رقم الفاتورة"
            placeholder="ابحث برقم الفاتورة…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            startIcon={<Search className="size-4" />}
            endIcon={
              searchInput ? (
                <button
                  type="button"
                  onClick={() => { setSearchInput(''); patch({ search: undefined }) }}
                  className="pointer-events-auto rounded p-0.5 text-fg-subtle hover:text-fg"
                  aria-label="مسح"
                >
                  <X className="size-4" />
                </button>
              ) : undefined
            }
            ltr
          />
        </form>
        <Input
          label="من تاريخ" type="date" ltr
          value={filter.from ?? ''}
          onChange={(e) => patch({ from: e.target.value || undefined })}
        />
        <Select
          label="الحالة"
          value={filter.status ?? ''}
          onChange={(e) => patch({ status: e.target.value || undefined })}
          options={Object.entries(SALE_STATUSES).map(([value, label]) => ({ value, label }))}
          placeholder="كل الحالات"
        />
      </div>

      <TableWrapper className={cn(isFetching && !isPending && 'opacity-60 transition-opacity')}>
        <Table>
          <Thead>
            <tr>
              <Th>رقم الفاتورة</Th>
              <Th>التاريخ</Th>
              <Th align="center">الأصناف</Th>
              <Th align="end">الإجمالي</Th>
              <Th>الدفع</Th>
              <Th>الحالة</Th>
              <Th>الكاشير</Th>
              <Th align="center">إجراءات</Th>
            </tr>
          </Thead>

          {isPending ? (
            <TableSkeleton rows={8} cols={8} />
          ) : (
            <Tbody>
              {data?.items.map((s) => (
                <Tr key={s.id}>
                  <Td>
                    <button
                      type="button"
                      onClick={() => setDetailId(s.id)}
                      className="font-mono text-xs font-medium text-primary hover:underline"
                      dir="ltr"
                    >
                      {s.reference}
                    </button>
                  </Td>
                  <Td className="whitespace-nowrap text-xs text-fg-muted">{formatDateTime(s.occurredAt)}</Td>
                  <Td align="center" className="tabular-nums" dir="ltr">{formatNumber(s.itemCount)}</Td>
                  <Td align="end" className="font-semibold tabular-nums" dir="ltr">{formatMoney(s.total)}</Td>
                  <Td className="text-xs text-fg-muted">
                    {s.payments.map((p) => PAYMENT_METHODS[p.method]).join('، ')}
                  </Td>
                  <Td>
                    <Badge tone={STATUS_TONE[s.status]}>{SALE_STATUSES[s.status]}</Badge>
                  </Td>
                  <Td className="text-sm text-fg-muted">{s.userName ?? '—'}</Td>
                  <Td align="center">
                    {can(PERMISSIONS.SALE_REFUND) && s.status !== 'REFUNDED' && s.status !== 'VOIDED' && (
                      <Button variant="ghost" size="icon" title="إرجاع" onClick={() => setReturnId(s.id)}>
                        <RotateCcw className="size-4" />
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          )}
        </Table>

        {!isPending && data?.items.length === 0 && (
          <EmptyState
            icon={<Receipt className="size-10" />}
            title="لا توجد فواتير"
            description="تظهر هنا كل عمليات البيع"
          />
        )}

        {data && data.items.length > 0 && (
          <Pagination
            page={data.page}
            totalPages={data.totalPages}
            total={data.total}
            pageSize={data.pageSize}
            onPageChange={(page) => setFilter((f) => ({ ...f, page }))}
            onPageSizeChange={(pageSize) => patch({ pageSize })}
          />
        )}
      </TableWrapper>

      {/* ── تفاصيل الفاتورة ── */}
      <Dialog
        open={!!detailId}
        onClose={() => setDetailId(null)}
        size="lg"
        title={detail?.reference}
        description={detail ? formatDateTime(detail.occurredAt) : undefined}
      >
        {detail && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 rounded-lg bg-bg-subtle p-4 text-sm sm:grid-cols-4">
              <div>
                <p className="text-xs text-fg-subtle">الكاشير</p>
                <p className="font-medium text-fg">{detail.userName ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs text-fg-subtle">الصندوق</p>
                <p className="font-medium text-fg">{detail.registerName ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs text-fg-subtle">المدفوع</p>
                <p className="font-medium text-fg" dir="ltr">{formatMoney(detail.paidTotal)}</p>
              </div>
              <div>
                <p className="text-xs text-fg-subtle">الباقي</p>
                <p className="font-medium text-fg" dir="ltr">{formatMoney(detail.changeDue)}</p>
              </div>
            </div>

            <TableWrapper>
              <Table>
                <Thead>
                  <tr>
                    <Th>المنتج</Th>
                    <Th align="center">الكمية</Th>
                    <Th align="end">السعر</Th>
                    <Th align="end">الإجمالي</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {detail.items.map((item) => (
                    <Tr key={item.id}>
                      <Td>
                        <p className="font-medium text-fg">{item.nameSnapshot}</p>
                        <p className="font-mono text-xs text-fg-subtle" dir="ltr">{item.skuSnapshot}</p>
                        {item.returnedQty > 0 && (
                          <Badge tone="warning" className="mt-1">
                            مُرجَع {formatNumber(item.returnedQty)}
                          </Badge>
                        )}
                      </Td>
                      <Td align="center" className="tabular-nums" dir="ltr">{formatNumber(item.quantity)}</Td>
                      <Td align="end" className="tabular-nums" dir="ltr">{formatMoney(item.unitPrice)}</Td>
                      <Td align="end" className="font-semibold tabular-nums" dir="ltr">{formatMoney(item.lineTotal)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableWrapper>

            <div className="space-y-1 border-t border-border pt-3 text-sm">
              <div className="flex justify-between text-fg-muted">
                <span>المجموع</span>
                <span dir="ltr">{formatMoney(detail.subtotal)}</span>
              </div>
              {detail.taxTotal > 0 && (
                <div className="flex justify-between text-fg-muted">
                  <span>الضريبة</span>
                  <span dir="ltr">{formatMoney(detail.taxTotal)}</span>
                </div>
              )}
              {detail.refundedTotal > 0 && (
                <div className="flex justify-between text-danger">
                  <span>المُرجَع</span>
                  <span dir="ltr">−{formatMoney(detail.refundedTotal)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-border pt-2 text-lg font-bold text-fg">
                <span>الإجمالي</span>
                <span dir="ltr">{formatMoney(detail.total)}</span>
              </div>
            </div>
          </div>
        )}
      </Dialog>

      <ReturnDialog saleId={returnId} onClose={() => setReturnId(null)} />
    </div>
  )
}
